/**
 * Free towel gift.
 *
 * Keeps one free towel per pair of glasses or Shield in the cart (the snippet counts
 * them into data-frames) and takes the towels out when no frames are left. The free
 * towel is a separate hidden $0 product, so discount codes never cancel it; extra
 * towels are the regular $5 towel. If the customer removes the gift themselves, it is
 * not added back in this session — the block offers an "Add free towel" button instead.
 */

// The snippet can be rendered twice on a page (drawer + cart page), so the script may run twice.
if (!customElements.get('towel-gift')) {
  const TOWEL_GIFT_AUTO_KEY = 'dinkTowelGiftAuto';
  const TOWEL_GIFT_DECLINED_KEY = 'dinkTowelGiftDeclined';

  function towelGiftStorage(action, key, value) {
    try {
      if (action === 'get') return sessionStorage.getItem(key);
      if (action === 'set') sessionStorage.setItem(key, value);
      if (action === 'remove') sessionStorage.removeItem(key);
    } catch (e) {
      // Storage can be unavailable (private mode) — the gift still works, just without memory.
    }
    return null;
  }

  class TowelGift extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-towel-gift-add]')?.addEventListener('click', () => {
        towelGiftStorage('remove', TOWEL_GIFT_DECLINED_KEY);
        this.addGift();
      });
      this.querySelector('[data-towel-extra-add]')?.addEventListener('click', () => this.addExtra());

      // The drawer and the cart page can both render this block — let only one sync.
      if (window.towelGiftBusy) return;
      this.sync();
    }

    get eligible() {
      return this.dataset.eligible === 'true';
    }

    get giftLine() {
      return this.dataset.giftLine ? parseInt(this.dataset.giftLine, 10) : null;
    }

    get giftQuantity() {
      return parseInt(this.dataset.giftQuantity, 10) || 0;
    }

    get frames() {
      return parseInt(this.dataset.frames, 10) || 0;
    }

    sync() {
      const autoAdded = towelGiftStorage('get', TOWEL_GIFT_AUTO_KEY) === '1';

      if (this.eligible && !this.giftLine) {
        if (autoAdded) {
          // We added it and it is gone — the customer removed it.
          towelGiftStorage('remove', TOWEL_GIFT_AUTO_KEY);
          towelGiftStorage('set', TOWEL_GIFT_DECLINED_KEY, '1');
          return;
        }
        if (towelGiftStorage('get', TOWEL_GIFT_DECLINED_KEY) === '1') return;
        this.addGift();
        return;
      }

      if (!this.eligible && this.giftLine) {
        towelGiftStorage('remove', TOWEL_GIFT_AUTO_KEY);
        towelGiftStorage('remove', TOWEL_GIFT_DECLINED_KEY);
        this.request(routes.cart_change_url, { line: this.giftLine, quantity: 0 });
        return;
      }

      // One free towel per frame.
      if (this.giftLine && this.giftQuantity !== this.frames) {
        this.request(routes.cart_change_url, { line: this.giftLine, quantity: this.frames });
      }
    }

    addGift() {
      towelGiftStorage('set', TOWEL_GIFT_AUTO_KEY, '1');
      this.request(routes.cart_add_url, {
        items: [{ id: Number(this.dataset.freeVariantId), quantity: this.frames, properties: { _dink_gift: '1' } }],
      });
    }

    addExtra() {
      const quantity = parseInt(this.querySelector('[data-towel-extra-quantity]')?.value, 10) || 1;
      this.request(routes.cart_add_url, {
        items: [{ id: Number(this.dataset.variantId), quantity }],
      });
    }

    getSections() {
      const sections = [
        {
          section: 'cart-drawer',
          target: '#CartDrawer-Body',
          selector: '#shopify-section-cart-drawer #CartDrawer-Body',
        },
        ...Cart.getLiveRegions().map((region) => ({
          section: region.section,
          target: region.id,
          selector: region.selector,
        })),
      ];

      const mainCart = document.getElementById('main-cart-items');
      if (mainCart) {
        const selector = `#shopify-section-${mainCart.dataset.id}`;
        sections.push({ section: mainCart.dataset.id, target: selector, selector });
      }

      return sections;
    }

    request(url, payload) {
      if (window.towelGiftBusy) return;
      window.towelGiftBusy = true;
      this.querySelectorAll('button').forEach((button) => button.setAttribute('disabled', ''));

      const sections = this.getSections();

      fetch(url, {
        ...fetchConfig(),
        body: JSON.stringify({
          ...payload,
          sections: sections.map((s) => s.section),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((state) => {
          if (state.status) throw new Error(state.description || state.message);
          this.render(sections, state.sections || {});
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch((error) => {
          console.error('[towel-gift]', error);
          this.querySelectorAll('button').forEach((button) => button.removeAttribute('disabled'));
        })
        .finally(() => {
          window.towelGiftBusy = false;
        });
    }

    render(sections, html) {
      // Re-render with the busy flag still set so the new blocks do not sync again on connect.
      sections.forEach(({ section, target, selector }) => {
        const element = document.querySelector(target);
        if (!element || !html[section]) return;
        const fresh = new DOMParser().parseFromString(html[section], 'text/html').querySelector(selector);
        if (fresh) element.innerHTML = fresh.innerHTML;
      });
    }
  }

  customElements.define('towel-gift', TowelGift);
}
